/**
 * Row search for DataTable, kept as a pure module so it can be unit-tested — the client
 * has no @testing-library/react (jsdom + vitest only), so anything that needs coverage
 * must be renderless.
 *
 * This exists instead of twreact-ui <Table>'s own `globalSearch` because that one
 * searches EVERY value on the row, ids and company_ids included, which produces
 * nonsense matches on a numeric query. Here the caller names the searchable columns.
 * See DataTable.jsx for the rest of the upstream story.
 */

/** Columns opt OUT with `searchable: false` (badges, action columns, raw ids). */
export function searchableKeys(columns) {
  return (columns || []).filter((c) => c && c.key && c.searchable !== false).map((c) => c.key);
}

/**
 * Case-insensitive substring match across `keys` only.
 *
 * Returns `rows` by identity when there is nothing to do — including when `keys` is
 * empty. Never degrade to "match everything" on an empty key set: that is precisely the
 * upstream bug this module routes around (an empty filter value made `includes('')`
 * true for every row, so the table looked filtered while showing all of it).
 */
export function filterRows(rows, query, keys) {
  const q = String(query || '').trim().toLowerCase();
  if (!q || !Array.isArray(rows) || !keys || keys.length === 0) return rows;
  return rows.filter((row) => row && keys.some((k) => String(row[k] ?? '').toLowerCase().includes(q)));
}

/**
 * The page to show once the row count changes under a paginated table.
 *
 * twreact-ui <Table> keeps its own current page and never re-checks it when `data`
 * shrinks, so searching from page 3 left "Page 3 of 1" over an empty grid. Clamped rather
 * than reset so toggling a row (which reloads the data) doesn't jump back to page 1.
 */
export function clampPage(page, total, limit) {
  const size = Number(limit) > 0 ? Number(limit) : 1;
  const pages = Math.max(1, Math.ceil((Number(total) || 0) / size));
  const p = Number.isInteger(Number(page)) && Number(page) > 0 ? Number(page) : 1;
  return Math.min(p, pages);
}
